<template>
  <!--    Fund Transfer Form-->
  <validation-observer
    ref="fundTransferForm"
    v-slot="{invalid}"
  >
    <sikanii-modal
      title="Transfer Funds"
      :show="fundTransferOpened"
      :show-overlay="searchingAccount"
      size="md"
      @onClose="closeFundTransferModal()"
    >
      <b-row class="align-items-start">
        <div class="col-12">
          <sikanii-button
            name="fundTransfer_action"
            variant="flat-danger"
            icon="XIcon"
            label="Clear Form"
            class="float-right"
            tool-tip-text="Clear Form"
            @click="resetFundTransferForm()"
          />
        </div>
      </b-row>
      <error-display :error="error" />
      <b>Sender</b>
      <div class="d-flex justify-content-center">
        <div
          v-if="sender"
          class="d-flex flex-column text-center"
        >
          <sikanii-profile-photo
            class="mb-50"
            size="100"
            :photo="sender.photo"
            preview
          />
          <b>{{ sender.full_name }}</b>
          <b v-if="$ability.can('view-customer-contribution-balance','savings_and_loans')">Balance: {{ $store.getters['auth/currency'] }}{{ sender.balance | currency }}</b>
          <b v-else>Balance: N/A</b>
        </div>
      </div>
      <sikanii-select
        v-model="fundTransfer.sender_account_type"
        label-name="label"
        value-name="id"
        name="sender_account_type"
        rules="required"
        label="Sender Account Type"
        placeholder="Select Account Type"
        :options="senderTypes"
        @input="resetForm('sender')"
      />
      <sikanii-select
        v-if="
          (fundTransfer.sender_account_type==='susu'
            || fundTransfer.sender_account_type==='savings')
            && sender && sender.cycles.length>1"
        v-model="fundTransfer.sender_account_id"
        label-name="title"
        value-name="id"
        name="cycle_id"
        label="Cycle"
        placeholder="Select Cycle"
        :options="sender?sender.cycles:[]"
        @input="handleSenderCycleChange"
      />
      <sikanii-input
        v-model="senderSearch"
        :show-label="false"
        placeholder="Search Sender Account"
        suffix-icon="SearchIcon"
        :readonly="!fundTransfer.sender_account_type"
        @onSuffixIconClick="(val)=>{handleSearchAccount(val,'sender')}"
        @onEnterPressed="(val)=>{handleSearchAccount(val,'sender')}"
      />
      <div class="mt-2" />
      <b>Recipient</b>
      <div class="d-flex justify-content-center">
        <div
          v-if="recipient"
          class="d-flex flex-column text-center"
        >
          <sikanii-profile-photo
            class="mb-50"
            size="100"
            :photo="recipient.photo"
            preview
          />
          <b>{{ recipient.full_name }}</b>
          <b v-if="$ability.can('view-customer-contribution-balance','savings_and_loans')">{{ fundTransfer.recipient_account_type==='loans'?'Total Paid':'Balance' }}: {{ $store.getters['auth/currency'] }}{{ fundTransfer.recipient_account_type==='loans'?recipient.total_paid:recipient.balance | currency }}</b>
          <b v-else>Balance: N/A</b>
        </div>
      </div>
      <sikanii-select
        v-model="fundTransfer.recipient_account_type"
        label-name="label"
        value-name="id"
        name="recipient_account_type"
        rules="required"
        label="Recipient Account Type"
        placeholder="Select Account Type"
        :options="recipientTypes"
        @input="resetForm('recipient')"
      />
      <sikanii-select
        v-if="(fundTransfer.recipient_account_type==='susu'
          || fundTransfer.recipient_account_type==='savings')
          && recipient && recipient.cycles.length>1"
        v-model="fundTransfer.recipient_account_id"
        label-name="title"
        value-name="id"
        name="cycle_id"
        label="Cycle"
        placeholder="Select Cycle"
        :options="recipient?recipient.cycles:[]"
        @input="handleRecipientCycleChange"
      />
      <sikanii-input
        v-model="recipientSearch"
        :show-label="false"
        placeholder="Search Recipient Account"
        suffix-icon="SearchIcon"
        :readonly="!fundTransfer.recipient_account_type"
        @onSuffixIconClick="(val)=>{handleSearchAccount(val,'recipient')}"
        @onEnterPressed="(val)=>{handleSearchAccount(val,'recipient')}"
      />
      <div class="mt-2" />
      <b>Transaction Details</b>
      <sikanii-input
        v-model="fundTransfer.amount"
        name="amount"
        placeholder="Enter fund transfer amount"
        rules="required|gt:0"
        label="Amount"
      />
      <sikanii-text-area
        v-model="fundTransfer.narration"
        placeholder="Enter Narration"
        label="Narration"
      />
      <b-form-checkbox v-model="fundTransfer.charge_commission">
        Charge Commission
      </b-form-checkbox>
      <div
        v-if="sameUserAccount"
        class="text-danger"
      >
        <small><b>You can not transfer funds between the same customer account.</b></small>
      </div>
      <template slot="modal-actions">
        <b-row class="float-right">
          <sikanii-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="closeFundTransferModal()"
          />
          <sikanii-button
            name="save_fundTransfer"
            label="Create"
            :disabled="invalid || sameUserAccount"
            :loading="fundTransferLoading"
            loading-text="Loading..."
            @click="createFundTransfer()"
          />
        </b-row>
      </template>
    </sikanii-modal>
  </validation-observer>
</template>

<script>
import {
  BRow, BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import SikaniiModal from '@/layouts/components/SikaniiModal.vue'
import SikaniiInput from '@/layouts/components/SikaniiInput.vue'
import SikaniiButton from '@/layouts/components/SikaniiButton.vue'
import showToast from '@/libs/mixins/showToast'
import SikaniiSelect from '@/layouts/components/SikaniiSelect.vue'
import logData from '@/libs/log'
import ErrorDisplay from '@/layouts/components/ErrorDisplay.vue'
import sexMixin from '@/libs/mixins/sexMixin'
import SikaniiTextArea from '@/layouts/components/SikaniiTextArea.vue'
import SikaniiProfilePhoto from '@/layouts/components/SikaniiProfilePhoto.vue'

const fundTransfer = {
  sender_id: null,
  recipient_id: null,
  sender_account_type: null,
  sender_account_id: null,
  recipient_account_id: null,
  recipient_account_type: null,
  amount: null,
  narration: null,
  charge_commission: true,
}
export default {
  name: 'FundTransferSetup',
  components: {
    SikaniiProfilePhoto,
    SikaniiTextArea,
    ErrorDisplay,
    SikaniiSelect,
    SikaniiButton,
    SikaniiInput,
    SikaniiModal,
    ValidationObserver,
    BRow,
    BFormCheckbox,
  },
  mixins: [showToast, sexMixin],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      senderTypes: [
        { label: 'Susu', id: 'susu' },
        { label: 'Savings', id: 'savings' },
      ],
      error: {},
      fundTransferOpened: false,
      updateFundTransferMode: false,
      fundTransferLoading: false,
      searchingAccount: false,
      selectedFundTransferId: null,
      fundTransfer: { ...fundTransfer },
      sender: null,
      recipient: null,
      senderSearch: null,
      recipientSearch: null,
    }
  },
  computed: {
    recipientTypes() {
      const options = [
        { label: 'Susu', id: 'susu' },
        { label: 'Savings', id: 'savings' },
      ]
      if (this.$store.getters['auth/hasLoanAccess']) {
        options.push({ label: 'Loans', id: 'loans' })
      }
      return options
    },
    sameUserAccount() {
      if (this.fundTransfer.recipient_id && this.fundTransfer.sender_id) {
        return (this.fundTransfer.sender_account_type === this.fundTransfer.recipient_account_type)
            && (this.fundTransfer.recipient_id === this.fundTransfer.sender_id)
      }
      return false
    },
  },
  watch: {
    modalOpened(opened) {
      this.fundTransferOpened = opened
    },
    editId(id) {
      this.selectedFundTransferId = id
    },
  },
  methods: {
    handleSenderCycleChange(id) {
      let cycle = this.sender.cycles.find(c => c.id === id)
      if (!cycle) {
        cycle = this.sender.cycles[0]
      }
      this.sender.balance = cycle.balance
    },
    handleRecipientCycleChange(id) {
      let cycle = this.recipient.cycles.find(c => c.id === id)
      if (!cycle) {
        cycle = this.recipient.cycles[0]
      }
      this.recipient.balance = cycle.balance
    },
    resetForm(type) {
      this.fundTransfer[`${type}_account_id`] = null
      this.fundTransfer[`${type}_id`] = null
      this[type] = null
      this[`${type}Search`] = null
    },
    handleSearchAccount(search, type) {
      if (!search) {
        this.showInfo('Please enter a search term')
        return
      }
      this.searchingAccount = true
      const accountType = this.fundTransfer[`${type}_account_type`]
      this.$http.get(`susu-savings/transfer-accounts?search=${search}&account_type=${accountType}`).then(res => {
        this[`${type}`] = res.data
        this.fundTransfer[`${type}_account_id`] = res.data.account_id
        this.fundTransfer[`${type}_id`] = res.data.id
      }).catch(err => {
        if (err.response.status === 404) {
          this.showInfo('No user found, try again')
        }
        this.error = err
      }).finally(() => {
        this.searchingAccount = false
      })
    },
    resetFundTransferForm() {
      this.fundTransfer = { ...fundTransfer }
      this.sender = null
      this.recipient = null
      this.$refs.fundTransferForm.reset()
    },
    closeFundTransferModal() {
      this.fundTransferOpened = false
      this.$emit('modalClosed')
    },
    createFundTransfer() {
      this.error = {}
      this.fundTransferLoading = true
      this.$http.post('susu-savings/transfer-funds', this.fundTransfer).then(res => {
        this.showSuccess('Created fund transfer successfully')
        this.fundTransferLoading = false
        this.$store.commit('susuSavings/addFundTransfer', res.data)
        this.resetFundTransferForm()
      })
        .catch(err => {
          logData(err)
          this.error = err
          if (err.response.status === 402) {
            this.showInfo('Transactions are closed')
          } else if (err.response.status === 403) {
            this.showInfo('You have reached the limit for total transactions allocated to you.')
          } else {
            this.showError('Failed to create fund transfer')
          }
          this.fundTransferLoading = false
        })
    },
  }
  ,
}
</script>
